import { useState } from 'react'

import { useI18n } from '@muse/i18n'
import { useLocation, useNavigate } from '@muse-web/router'

import { sendEvent } from '@core/analytics'
import type { TAudioSimple } from '@core/models'
import { getAuthorPageUrl } from '@core/routes'
// `CollectionAddIcon` (audio-web's choice) exists in the DS but its icons barrel is generated and
// doesn't export it; `AddToListIcon` is the same list-plus artwork.
import { AddToListIcon, DownloadIcon, ReplaceIcon, toast, TrashBinIcon } from '@ds/components'
import { getAudioEditPageUrl } from '@features/audio'
import {
    AsyncDeleteAudioDialog,
    useDeleteAudio,
    type TReplaceSourceError,
    useReplaceAudioSource,
} from '@features/audio-edit'
import { canDownloadAudio, useDownloadDialog } from '@features/audio-download'
import { useAddToCollection } from '@features/collection'
import {
    AsyncPromoReleaseWarningDialog,
    checkPromoReleaseContain,
    getPromoReleaseNames,
} from '@features/promo-page'
import { useCurrentUser } from '@features/session'

import { OWNER_MORE_ACTIONS } from '../constants/audioActions'
import type { TAudioAction, TAudioActionId } from '../types/audioActionTypes'

type TUseAudioHeroActionsParams = {
    audio: TAudioSimple
    isOwner: boolean
}

/**
 * Called unconditionally — each owner branch no-ops for a visitor, which also keeps an in-flight
 * upload alive if a 401 downgrades the session mid-way.
 */
export const useAudioHeroActions = ({ audio, isOwner }: TUseAudioHeroActionsParams) => {
    const { t } = useI18n()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { username: myUsername, id: myId } = useCurrentUser()
    const addToCollection = useAddToCollection()
    const download = useDownloadDialog()
    const deleteAudio = useDeleteAudio()

    const [isSheetOpen, setIsSheetOpen] = useState(false)
    const [isDeleteOpen, setIsDeleteOpen] = useState(false)
    const [isPromoWarningOpen, setIsPromoWarningOpen] = useState(false)
    const [promoReleaseNames, setPromoReleaseNames] = useState<string[]>([])

    const showReplaceSourceError = (reason: TReplaceSourceError) => {
        if (reason === 'invalid-file') {
            toast.error(t('i18n.audioReplaceSourceErrorInvalidFile'))

            return
        }

        if (reason === 'not-found') {
            toast.error(t('i18n.audioReplaceSourceErrorNotFound'))

            return
        }

        if (reason === 'unauthorized') {
            toast.error(t('i18n.audioReplaceSourceErrorUnauthorized'))

            return
        }

        toast.error(t('i18n.audioReplaceSourceError'))
    }

    const showProcessingTimeout = () => {
        toast.error(t('i18n.audioReplaceSourceTimeout'))
    }

    const replaceSource = useReplaceAudioSource({
        audioId: audio.id,
        authorUsername: audio.authorUsername,
        onError: showReplaceSourceError,
        onProcessingTimeout: showProcessingTimeout,
    })

    const handleEdit = () => {
        sendEvent({ name: 'audio_edit_click', payload: { audio: audio.id } })
        navigate(
            getAudioEditPageUrl({
                authorUsername: audio.authorUsername,
                audioSlug: audio.slug,
                from: pathname,
            }),
        )
    }

    const handleAddToCollection = () => {
        addToCollection.open({
            id: audio.id,
            title: audio.title,
            authorUsername: audio.authorUsername,
        })
    }

    const handleDownload = () => {
        download.open(
            {
                id: audio.id,
                title: audio.title,
                authorUsername: audio.authorUsername,
                authorId: audio.authorId,
                avatarUrl: audio.avatarUrl,
            },
            'player_audio',
        )
    }

    const handleDeleteRequest = async () => {
        AsyncDeleteAudioDialog.preload()
        AsyncPromoReleaseWarningDialog.preload()

        try {
            const releases = await checkPromoReleaseContain({ id: audio.id, type: 'audio' })
            const names = getPromoReleaseNames(releases)

            if (names.length > 0) {
                setPromoReleaseNames(names)
                setIsPromoWarningOpen(true)

                return
            }
        } catch {
            // A failed pre-check must not block the owner from deleting their audio.
        }

        setIsDeleteOpen(true)
    }

    const openDeleteAfterPromoWarning = () => {
        setIsDeleteOpen(true)
    }

    const confirmDelete = async () => {
        try {
            await deleteAudio.mutateAsync({
                audioId: audio.id,
                audioSlug: audio.slug,
                authorUsername: audio.authorUsername,
            })
            setIsDeleteOpen(false)
            navigate(getAuthorPageUrl({ authorUsername: audio.authorUsername }), {
                isForceHard: true,
            })
        } catch {
            setIsDeleteOpen(false)
            toast.error(t('i18n.audioDeleteError'))
        }
    }

    const actionById: Record<TAudioActionId, { isAvailable: boolean; action: TAudioAction }> = {
        'replace-source': {
            isAvailable: isOwner,
            action: {
                id: 'replace-source',
                label: t('i18n.audioActionReplaceSource'),
                Icon: ReplaceIcon,
                onSelect: replaceSource.openFilePicker,
                // Picking a second file mid-upload is ignored by the hook — say so instead of
                // swallowing the click.
                isDisabled: replaceSource.isUploading,
            },
        },
        'add-to-collection': {
            isAvailable: isOwner && myId !== 0,
            action: {
                id: 'add-to-collection',
                label: t('i18n.audioActionAddToCollection'),
                Icon: AddToListIcon,
                onSelect: handleAddToCollection,
            },
        },
        download: {
            isAvailable: isOwner && canDownloadAudio(audio, myUsername),
            action: {
                id: 'download',
                label: t('i18n.audioPlayerDownload'),
                Icon: DownloadIcon,
                onSelect: handleDownload,
            },
        },
        delete: {
            isAvailable: isOwner,
            action: {
                id: 'delete',
                label: t('i18n.commonDelete'),
                Icon: TrashBinIcon,
                onSelect: handleDeleteRequest,
            },
        },
    }

    const actions = OWNER_MORE_ACTIONS.filter(id => actionById[id].isAvailable).map(
        id => actionById[id].action,
    )

    const getUploadProgressLabel = () => {
        if (replaceSource.progress === null) {
            return undefined
        }

        if (replaceSource.isProcessing) {
            return t('i18n.audioReplaceSourceProcessing')
        }

        return `${t('i18n.audioReplaceSourceUploading')} ${replaceSource.progress}%`
    }

    const openSheet = () => {
        setIsSheetOpen(true)
    }

    // audio-web pluralizes "release"/"releases" in the sentence itself, so the two variants are
    // separate keys — `@muse/i18n` has no plural rules wired in this repo.
    const getPromoWarningDescription = () => {
        const lead =
            promoReleaseNames.length === 1
                ? t('i18n.audioPromoReleaseDeleteDescriptionOne')
                : t('i18n.audioPromoReleaseDeleteDescriptionMany')

        return `${lead} ${promoReleaseNames.join(', ')}.`
    }

    return {
        actions,
        handleEdit,
        canAddToCollection: myId !== 0,
        handleAddToCollection,
        isSheetOpen,
        setIsSheetOpen,
        openSheet,
        fileInputRef: replaceSource.fileInputRef,
        onSelectSourceFile: replaceSource.replaceSource,
        uploadProgressLabel: getUploadProgressLabel(),
        isDeleteOpen,
        setIsDeleteOpen,
        confirmDelete,
        isDeleting: deleteAudio.isPending,
        promoWarningDescription: getPromoWarningDescription(),
        isPromoWarningOpen,
        setIsPromoWarningOpen,
        openDeleteAfterPromoWarning,
    }
}
